import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, RefreshCw, Trash2, ChevronLeft, ChevronRight, Users, UserPlus, Clock, CheckCircle, XCircle, Eye, Copy, ExternalLink, Facebook, BarChart3, Lock, Shield, LogOut, Download } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_LOGIN = process.env.REACT_APP_ADMIN_LOGIN || 'capitara';
const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || 'admin3894';
const FB_PIXEL_ID_2 = '1934450234107970';
const GOOGLE_ADS_ID = 'AW-17767732872';

/* ---- Matrix Rain Canvas ---- */
function MatrixRain() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animId;

    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener('resize', resize);

    const chars = 'アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ$€¥£₿';
    const fontSize = 14;
    const columns = Math.floor(canvas.width / fontSize);
    const drops = Array(columns).fill(1);

    function draw() {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.05)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.font = `${fontSize}px monospace`;

      for (let i = 0; i < drops.length; i++) {
        const char = chars[Math.floor(Math.random() * chars.length)];
        const x = i * fontSize;
        const y = drops[i] * fontSize;

        // Bright head - greenish-gray
        ctx.fillStyle = `rgba(140, 180, 150, ${0.5 + Math.random() * 0.3})`;
        ctx.fillText(char, x, y);

        // Dim trail
        if (drops[i] > 1) {
          const trailChar = chars[Math.floor(Math.random() * chars.length)];
          ctx.fillStyle = 'rgba(100, 140, 110, 0.15)';
          ctx.fillText(trailChar, x, y - fontSize);
        }
        if (drops[i] > 2) {
          const trailChar2 = chars[Math.floor(Math.random() * chars.length)];
          ctx.fillStyle = 'rgba(80, 120, 90, 0.07)';
          ctx.fillText(trailChar2, x, y - fontSize * 2);
        }

        if (y > canvas.height && Math.random() > 0.975) {
          drops[i] = 0;
        }
        drops[i]++;
      }
      animId = requestAnimationFrame(draw);
    }
    draw();

    return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', resize); };
  }, []);

  return <canvas ref={canvasRef} style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0 }} />;
}

const STATUS_MAP = {
  new: { label: 'Новый', color: '#10B981', bg: 'rgba(16,185,129,0.12)' },
  in_progress: { label: 'В работе', color: '#F59E0B', bg: 'rgba(245,158,11,0.12)' },
  completed: { label: 'Завершён', color: '#3B82F6', bg: 'rgba(59,130,246,0.12)' },
  rejected: { label: 'Отклонён', color: '#EF4444', bg: 'rgba(239,68,68,0.12)' },
  duplicate: { label: 'Дубль', color: '#8B5CF6', bg: 'rgba(139,92,246,0.12)' },
};

function copyToClipboard(text) {
  navigator.clipboard.writeText(text);
}

export default function AdminPage() {
  const [authorized, setAuthorized] = useState(false);
  const [loginForm, setLoginForm] = useState({ login: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('leads');
  const [selectedLead, setSelectedLead] = useState(null);
  const [copied, setCopied] = useState('');

  useEffect(() => {
    const saved = sessionStorage.getItem('capitara_admin');
    if (saved === 'true') setAuthorized(true);
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    if (loginForm.login === ADMIN_LOGIN && loginForm.password === ADMIN_PASSWORD) {
      setAuthorized(true);
      setLoginError('');
      sessionStorage.setItem('capitara_admin', 'true');
    } else {
      setLoginError('Неверный логин или пароль');
    }
  };

  const handleLogout = () => {
    setAuthorized(false);
    sessionStorage.removeItem('capitara_admin');
    setLoginForm({ login: '', password: '' });
  };

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ code: ADMIN_PASSWORD, page, limit: 50 });
      if (search) params.set('search', search);
      if (statusFilter !== 'all') params.set('status', statusFilter);
      const res = await fetch(`${API_URL}/api/admin/leads?${params}`);
      const data = await res.json();
      setLeads(data.leads || []);
      setTotalPages(data.pages || 1);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, [search, statusFilter, page]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/stats?code=${ADMIN_PASSWORD}`);
      const data = await res.json();
      setStats(data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    if (authorized) {
      fetchLeads();
      fetchStats();
    }
  }, [authorized, fetchLeads, fetchStats]);

  const updateStatus = async (leadId, newStatus) => {
    await fetch(`${API_URL}/api/admin/leads/${leadId}/status?code=${ADMIN_PASSWORD}&new_status=${newStatus}`, { method: 'PATCH' });
    fetchLeads();
    fetchStats();
  };

  const deleteLead = async (leadId) => {
    if (!window.confirm('Удалить эту заявку?')) return;
    await fetch(`${API_URL}/api/admin/leads/${leadId}?code=${ADMIN_PASSWORD}`, { method: 'DELETE' });
    setSelectedLead(null);
    fetchLeads();
    fetchStats();
  };

  const handleCopy = (text, label) => {
    copyToClipboard(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  if (!authorized) {
    return (
      <div style={styles.authScreen}>
        <MatrixRain />
        <form onSubmit={handleLogin} style={styles.authCard} data-testid="login-form">
          <Lock size={48} color="#0F0" />
          <h2 style={styles.authTitle}>CAPITARA ADMIN</h2>
          <p style={styles.authText}>Введите логин и пароль</p>
          <input
            data-testid="login-input"
            type="text"
            placeholder="Логин"
            value={loginForm.login}
            onChange={e => setLoginForm({ ...loginForm, login: e.target.value })}
            style={styles.authInput}
            autoFocus
          />
          <input
            data-testid="password-input"
            type="password"
            placeholder="Пароль"
            value={loginForm.password}
            onChange={e => setLoginForm({ ...loginForm, password: e.target.value })}
            style={styles.authInput}
          />
          {loginError && <div style={styles.authError}>{loginError}</div>}
          <button type="submit" style={styles.authBtn} data-testid="login-btn">Войти</button>
        </form>
      </div>
    );
  }

  return (
    <div className="admin-root" data-testid="admin-page">
      <MatrixRain />
      {/* Sidebar - desktop / Top bar - mobile */}
      <aside className="admin-sidebar">
        <div className="admin-sidebar-top">
          <div style={styles.sidebarLogo}>
            <Shield size={24} color="#10B981" />
            <span style={styles.sidebarLogoText}>CAPITARA</span>
          </div>
          <p className="admin-sub-desktop" style={styles.sidebarSub}>Admin Panel</p>
        </div>
        <nav className="admin-nav">
          <button
            data-testid="tab-leads"
            className={`admin-nav-btn ${activeTab === 'leads' ? 'active' : ''}`}
            onClick={() => setActiveTab('leads')}
          >
            <Users size={18} /> <span className="admin-nav-label">Заявки</span>
          </button>
          <button
            data-testid="tab-ads"
            className={`admin-nav-btn ${activeTab === 'ads' ? 'active' : ''}`}
            onClick={() => setActiveTab('ads')}
          >
            <BarChart3 size={18} /> <span className="admin-nav-label">Реклама</span>
          </button>
        </nav>
        <button onClick={handleLogout} className="admin-logout-btn" data-testid="logout-btn">
          <LogOut size={16} /> <span className="admin-nav-label">Выйти</span>
        </button>
      </aside>

      {/* Main */}
      <main className="admin-main">
        {/* Stats Bar */}
        {stats && (
          <div style={styles.statsBar} data-testid="admin-stats">
            <StatCard icon={<Users size={20} />} label="Всего заявок" value={stats.total} color="#0F0" />
            <StatCard icon={<UserPlus size={20} />} label="Новые" value={stats.new} color="#0F0" />
            <StatCard icon={<Clock size={20} />} label="В работе" value={stats.in_progress} color="#FFD700" />
            <StatCard icon={<CheckCircle size={20} />} label="Завершено" value={stats.completed} color="#00BFFF" />
            <StatCard icon={<XCircle size={20} />} label="Отклонено" value={stats.rejected} color="#FF4444" />
            <StatCard icon={<Copy size={20} />} label="Дубли" value={stats.duplicate} color="#8B5CF6" />
          </div>
        )}

        {activeTab === 'leads' && (
          <div style={styles.panel}>
            {/* Toolbar */}
            <div style={styles.toolbar} data-testid="leads-toolbar">
              <div style={styles.searchWrap}>
                <Search size={16} color="#9CA3AF" style={{ position: 'absolute', left: 12, top: 10 }} />
                <input
                  data-testid="search-input"
                  placeholder="Поиск по имени, телефону, email..."
                  value={search}
                  onChange={e => { setSearch(e.target.value); setPage(1); }}
                  style={styles.searchInput}
                />
              </div>
              <select
                data-testid="status-filter"
                value={statusFilter}
                onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
                style={styles.select}
              >
                <option value="all">Все статусы</option>
                <option value="new">Новые</option>
                <option value="in_progress">В работе</option>
                <option value="completed">Завершено</option>
                <option value="rejected">Отклонено</option>
                <option value="duplicate">Дубли</option>
              </select>
              <button data-testid="refresh-btn" onClick={() => { fetchLeads(); fetchStats(); }} style={styles.refreshBtn}>
                <RefreshCw size={16} className={loading ? 'spin' : ''} /> Обновить
              </button>
              <button data-testid="export-csv-btn" onClick={() => {
                const params = new URLSearchParams({ code: ADMIN_PASSWORD });
                if (statusFilter !== 'all') params.append('status', statusFilter);
                window.open(`${API_URL}/api/admin/leads/export?${params.toString()}`, '_blank');
              }} style={styles.exportBtn}>
                <Download size={16} /> CSV
              </button>
            </div>

            {/* Table */}
            <div style={styles.tableWrap}>
              <table style={styles.table} data-testid="leads-table">
                <thead>
                  <tr>
                    <th style={styles.th}>Имя</th>
                    <th style={styles.th}>Телефон</th>
                    <th style={styles.th}>Email</th>
                    <th style={styles.th}>Тип</th>
                    <th style={styles.th}>Сумма</th>
                    <th style={styles.th}>Статус</th>
                    <th style={styles.th}>Дата</th>
                    <th style={styles.th}>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.length === 0 && (
                    <tr><td colSpan={8} style={styles.empty}>Заявки не найдены</td></tr>
                  )}
                  {leads.map(lead => (
                    <tr key={lead.id} style={styles.tr}>
                      <td style={styles.td}>{lead.name}</td>
                      <td style={styles.td}>{lead.phone}</td>
                      <td style={styles.td}>{lead.email}</td>
                      <td style={styles.td}>{formatFraudType(lead.fraud_type)}</td>
                      <td style={styles.td}>{lead.amount || '—'}</td>
                      <td style={styles.td}>
                        <select
                          data-testid={`status-select-${lead.id}`}
                          value={lead.status || 'new'}
                          onChange={e => updateStatus(lead.id, e.target.value)}
                          style={{
                            ...styles.statusBadge,
                            color: STATUS_MAP[lead.status || 'new']?.color,
                            background: STATUS_MAP[lead.status || 'new']?.bg,
                          }}
                        >
                          {Object.entries(STATUS_MAP).map(([k, v]) => (
                            <option key={k} value={k}>{v.label}</option>
                          ))}
                        </select>
                      </td>
                      <td style={styles.td}>{formatDate(lead.created_at)}</td>
                      <td style={styles.td}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button data-testid={`view-lead-${lead.id}`} onClick={() => setSelectedLead(lead)} style={styles.iconBtn} title="Подробнее"><Eye size={15} /></button>
                          <button data-testid={`delete-lead-${lead.id}`} onClick={() => deleteLead(lead.id)} style={{ ...styles.iconBtn, color: '#EF4444' }} title="Удалить"><Trash2 size={15} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={styles.pagination} data-testid="pagination">
                <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} style={styles.pageBtn}><ChevronLeft size={16} /></button>
                <span style={styles.pageInfo}>{page} / {totalPages}</span>
                <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} style={styles.pageBtn}><ChevronRight size={16} /></button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'ads' && (
          <div style={styles.panel} data-testid="ads-panel">
            <h2 style={styles.panelTitle}>Рекламные интеграции</h2>
            <p style={styles.panelDesc}>Данные для настройки и запуска рекламных кампаний</p>

            {/* Facebook Pixel 2 */}
            <div style={styles.adCard}>
              <div style={styles.adCardHeader}>
                <Facebook size={22} color="#1877F2" />
                <h3 style={styles.adCardTitle}>Facebook Pixel 2</h3>
                <span style={styles.activeBadge}>Активен</span>
              </div>
              <div style={styles.adInfoGrid}>
                <InfoRow label="Pixel ID" value={FB_PIXEL_ID_2} onCopy={() => handleCopy(FB_PIXEL_ID_2, 'pixel2')} copied={copied === 'pixel2'} />
                <InfoRow label="Статус" value="Установлен и работает" />
                <InfoRow label="Привязка" value="Страница /successful (после отправки формы)" />
                <InfoRow label="Telegram-бот" value="Бот 2 (заявки с ?source=pixel2)" />
              </div>
              <div style={styles.eventsSection}>
                <h4 style={styles.eventsTitle}>Настроенные события</h4>
                <div style={styles.eventsList}>
                  <EventTag name="PageView" desc="Загрузка любой страницы" />
                  <EventTag name="Lead" desc="Успешная отправка заявки (стр. /successful)" />
                </div>
              </div>
              <div style={styles.codeSection}>
                <h4 style={styles.eventsTitle}>Код пикселя</h4>
                <div style={styles.codeBlock}>
                  <code style={styles.codeText}>{FB_PIXEL_ID_2}</code>
                  <button style={styles.copyCodeBtn} onClick={() => handleCopy(FB_PIXEL_ID_2, 'fb2-code')}>
                    <Copy size={14} /> {copied === 'fb2-code' ? 'Скопировано!' : 'Копировать'}
                  </button>
                </div>
              </div>
              <div style={styles.instructionBlock}>
                <h4 style={styles.eventsTitle}>Как работает</h4>
                <ol style={styles.instructionList}>
                  <li>Клиент заполняет <b>форму заявки</b> и нажимает «Отправить»</li>
                  <li>После успешной отправки — редирект на <b>/successful</b></li>
                  <li>Событие <b>Lead</b> отправляется в этот пиксель (только при реальной заявке)</li>
                  <li>Заявка приходит в <b>Telegram-бот 2</b> (если source=pixel2)</li>
                  <li>Прямой заход на /successful <b>не считается</b> лидом</li>
                </ol>
              </div>
            </div>

            {/* Google Ads */}
            <div style={styles.adCard}>
              <div style={styles.adCardHeader}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M12.48 10.92v3.28h7.84c-.24 1.84-.88 3.24-1.88 4.24-1.2 1.2-3.04 2.48-5.96 2.48-4.76 0-8.48-3.84-8.48-8.6 0-4.76 3.72-8.6 8.48-8.6 2.56 0 4.44.96 5.8 2.24l2.36-2.36C18.36 1.52 15.84 0 12.48 0 5.64 0 .04 5.36.04 12s5.6 12 12.44 12c3.64 0 6.4-1.2 8.56-3.44 2.2-2.2 2.92-5.32 2.92-7.84 0-.8-.04-1.44-.16-2.04H12.48z" fill="#4285F4"/></svg>
                <h3 style={styles.adCardTitle}>Google Ads</h3>
                <span style={styles.activeBadge}>Активен</span>
              </div>
              <div style={styles.adInfoGrid}>
                <InfoRow label="Google Ads ID" value={GOOGLE_ADS_ID} onCopy={() => handleCopy(GOOGLE_ADS_ID, 'gads')} copied={copied === 'gads'} />
                <InfoRow label="Тег" value="gtag.js (Global Site Tag)" />
              </div>
              <div style={styles.eventsSection}>
                <h4 style={styles.eventsTitle}>Настроенные события</h4>
                <div style={styles.eventsList}>
                  <EventTag name="page_view" desc="Просмотр страницы" />
                  <EventTag name="conversion" desc="Отправка формы (конверсия)" />
                </div>
              </div>
              <div style={styles.codeSection}>
                <h4 style={styles.eventsTitle}>Код конверсии для Google Ads</h4>
                <div style={styles.codeBlock}>
                  <code style={styles.codeText}>{`gtag('event', 'conversion', { 'send_to': '${GOOGLE_ADS_ID}/CONVERSION_LABEL' });`}</code>
                  <button style={styles.copyCodeBtn} onClick={() => handleCopy(`gtag('event', 'conversion', { 'send_to': '${GOOGLE_ADS_ID}/CONVERSION_LABEL' });`, 'gads-conv')}>
                    <Copy size={14} /> {copied === 'gads-conv' ? 'Скопировано!' : 'Копировать'}
                  </button>
                </div>
              </div>
              <div style={styles.instructionBlock}>
                <h4 style={styles.eventsTitle}>Инструкция по настройке</h4>
                <ol style={styles.instructionList}>
                  <li>Откройте <b>Google Ads</b> → Инструменты → Конверсии</li>
                  <li>Создайте действие-конверсию для <b>«Отправка формы»</b></li>
                  <li>Скопируйте <b>Conversion Label</b> и замените в коде выше</li>
                  <li>Тег gtag.js уже установлен на сайте с ID: <code>{GOOGLE_ADS_ID}</code></li>
                  <li>URL целевой страницы: <code>https://capitaraglobalx.com</code></li>
                </ol>
              </div>
            </div>

            {/* Quick Reference */}
            <div style={styles.adCard}>
              <div style={styles.adCardHeader}>
                <ExternalLink size={22} color="#10B981" />
                <h3 style={styles.adCardTitle}>Быстрая справка</h3>
              </div>
              <div style={styles.quickRef}>
                <QuickRefRow label="Домен сайта" value="capitaraglobalx.com" onCopy={() => handleCopy('capitaraglobalx.com', 'domain')} copied={copied === 'domain'} />
                <QuickRefRow label="URL целевой страницы" value="https://capitaraglobalx.com" onCopy={() => handleCopy('https://capitaraglobalx.com', 'url')} copied={copied === 'url'} />
                <QuickRefRow label="Facebook Pixel ID" value={FB_PIXEL_ID_2} onCopy={() => handleCopy(FB_PIXEL_ID_2, 'ref-pixel')} copied={copied === 'ref-pixel'} />
                <QuickRefRow label="Google Ads ID" value={GOOGLE_ADS_ID} onCopy={() => handleCopy(GOOGLE_ADS_ID, 'ref-gads')} copied={copied === 'ref-gads'} />
                <QuickRefRow label="Telegram бот" value="Уведомления о заявках включены" />
                <QuickRefRow label="Языки сайта" value="Русский, English, العربية" />
              </div>
            </div>
          </div>
        )}

        {/* Lead Detail Modal */}
        {selectedLead && (
          <div style={styles.modalOverlay} onClick={() => setSelectedLead(null)} data-testid="lead-detail-modal">
            <div style={styles.modal} onClick={e => e.stopPropagation()}>
              <h3 style={styles.modalTitle}>Заявка: {selectedLead.name}</h3>
              <div style={styles.modalGrid}>
                <ModalField label="Имя" value={selectedLead.name} />
                <ModalField label="Телефон" value={selectedLead.phone} />
                <ModalField label="Email" value={selectedLead.email} />
                <ModalField label="Telegram" value={selectedLead.telegram || '—'} />
                <ModalField label="Тип мошенничества" value={formatFraudType(selectedLead.fraud_type)} />
                <ModalField label="Сумма убытка" value={selectedLead.amount || '—'} />
                <ModalField label="Когда произошло" value={formatTimeElapsed(selectedLead.time_elapsed)} />
                <ModalField label="Статус" value={STATUS_MAP[selectedLead.status || 'new']?.label} />
                <ModalField label="Дата" value={formatDate(selectedLead.created_at)} />
              </div>
              <div style={styles.modalDescBlock}>
                <p style={styles.modalDescLabel}>Описание ситуации:</p>
                <p style={styles.modalDescText}>{selectedLead.description}</p>
              </div>
              <div style={styles.modalActions}>
                <button style={styles.modalDeleteBtn} onClick={() => deleteLead(selectedLead.id)}>
                  <Trash2 size={14} /> Удалить
                </button>
                <button style={styles.modalCloseBtn} onClick={() => setSelectedLead(null)}>Закрыть</button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function StatCard({ icon, label, value, color }) {
  return (
    <div style={{ ...styles.statCard, borderLeft: `3px solid ${color}` }}>
      <div style={{ color }}>{icon}</div>
      <div>
        <div style={styles.statValue}>{value}</div>
        <div style={styles.statLabel}>{label}</div>
      </div>
    </div>
  );
}

function InfoRow({ label, value, onCopy, copied }) {
  return (
    <div style={styles.infoRow}>
      <span style={styles.infoLabel}>{label}</span>
      <div style={styles.infoValueWrap}>
        <span style={styles.infoValue}>{value}</span>
        {onCopy && (
          <button onClick={onCopy} style={styles.copySmallBtn}>
            <Copy size={12} /> {copied ? 'Скопировано!' : ''}
          </button>
        )}
      </div>
    </div>
  );
}

function EventTag({ name, desc }) {
  return (
    <div style={styles.eventTag}>
      <code style={styles.eventName}>{name}</code>
      <span style={styles.eventDesc}>{desc}</span>
    </div>
  );
}

function QuickRefRow({ label, value, onCopy, copied }) {
  return (
    <div style={styles.quickRefRow}>
      <span style={styles.quickRefLabel}>{label}</span>
      <div style={styles.quickRefValueWrap}>
        <code style={styles.quickRefValue}>{value}</code>
        {onCopy && (
          <button onClick={onCopy} style={styles.copySmallBtn}>
            <Copy size={12} /> {copied ? '!' : ''}
          </button>
        )}
      </div>
    </div>
  );
}

function ModalField({ label, value }) {
  return (
    <div style={styles.modalField}>
      <span style={styles.modalFieldLabel}>{label}</span>
      <span style={styles.modalFieldValue}>{value}</span>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const FRAUD_LABELS = { investment: 'Инвестиции', pyramid: 'Пирамиды', internet: 'Интернет', crypto: 'Крипто' };
const TIME_LABELS = { month: 'Месяц назад', '2months': '2 мес. назад', '3months': '3 мес. назад', more: 'Более 3 мес.' };

function formatFraudType(type) {
  return FRAUD_LABELS[type] || type || '—';
}

function formatTimeElapsed(time) {
  return TIME_LABELS[time] || time || '—';
}

const styles = {
  authScreen: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#000' },
  authCard: { textAlign: 'center', padding: 48, background: 'rgba(0,0,0,0.5)', borderRadius: 16, border: '1px solid rgba(0,255,70,0.2)', position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, minWidth: 320 },
  authTitle: { color: '#fff', marginTop: 12, fontSize: 22, fontWeight: 600, fontFamily: 'monospace' },
  authText: { color: 'rgba(255,255,255,0.5)', fontSize: 14, marginBottom: 8 },
  authInput: { width: '100%', padding: '12px 16px', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(0,255,70,0.2)', borderRadius: 10, color: '#fff', fontSize: 14, outline: 'none', fontFamily: 'monospace' },
  authError: { color: '#EF4444', fontSize: 13, padding: '6px 0' },
  authBtn: { width: '100%', padding: '12px', background: 'rgba(0,255,70,0.15)', border: '1px solid rgba(0,255,70,0.3)', borderRadius: 10, color: '#0F0', fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'monospace', marginTop: 4 },
  root: { display: 'flex', minHeight: '100vh', background: '#000', fontFamily: "'Inter', -apple-system, sans-serif" },
  sidebar: { width: 220, background: 'rgba(0,0,0,0.55)', borderRight: '1px solid rgba(0,255,70,0.12)', padding: '28px 20px', position: 'fixed', top: 0, bottom: 0, left: 0, zIndex: 50, display: 'flex', flexDirection: 'column' },
  sidebarLogo: { display: 'flex', alignItems: 'center', gap: 10 },
  sidebarLogoText: { color: '#fff', fontSize: 18, fontWeight: 700, letterSpacing: 2, fontFamily: 'monospace' },
  sidebarSub: { color: 'rgba(255,255,255,0.4)', fontSize: 11, marginTop: 4, letterSpacing: 1, textTransform: 'uppercase', fontFamily: 'monospace' },
  sidebarNav: { marginTop: 40, display: 'flex', flexDirection: 'column', gap: 6, flex: 1 },
  navBtn: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: 14, borderRadius: 8, cursor: 'pointer', transition: 'all .2s', textAlign: 'left' },
  navBtnActive: { background: 'rgba(0,255,70,0.08)', color: '#fff' },
  main: { flex: 1, marginLeft: 220, padding: '28px 32px', position: 'relative', zIndex: 2 },
  statsBar: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, marginBottom: 24 },
  statCard: { background: 'rgba(0,0,0,0.45)', borderRadius: 10, padding: '16px 18px', display: 'flex', alignItems: 'center', gap: 14, border: '1px solid rgba(0,255,70,0.1)' },
  statValue: { color: '#fff', fontSize: 24, fontWeight: 700, fontFamily: 'monospace' },
  statLabel: { color: 'rgba(255,255,255,0.5)', fontSize: 12, marginTop: 2 },
  panel: { background: 'rgba(0,0,0,0.45)', borderRadius: 12, padding: 24, border: '1px solid rgba(0,255,70,0.1)' },
  panelTitle: { color: '#fff', fontSize: 20, fontWeight: 600, marginBottom: 4 },
  panelDesc: { color: 'rgba(255,255,255,0.45)', fontSize: 13, marginBottom: 24 },
  toolbar: { display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' },
  searchWrap: { position: 'relative', flex: 1, minWidth: 200 },
  searchInput: { width: '100%', padding: '8px 12px 8px 36px', background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#fff', fontSize: 13, outline: 'none', fontFamily: 'monospace' },
  select: { padding: '8px 12px', background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#fff', fontSize: 13, outline: 'none' },
  refreshBtn: { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: '#fff', fontSize: 13, cursor: 'pointer' },
  exportBtn: { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: 'rgba(0,255,70,0.1)', border: '1px solid rgba(0,255,70,0.25)', borderRadius: 8, color: '#00ff46', fontSize: 13, cursor: 'pointer', fontWeight: 500 },
  tableWrap: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '10px 12px', fontSize: 11, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: 1, borderBottom: '1px solid rgba(255,255,255,0.08)' },
  tr: { borderBottom: '1px solid rgba(255,255,255,0.04)', transition: 'background .15s' },
  td: { padding: '12px', fontSize: 13, color: 'rgba(255,255,255,0.8)' },
  empty: { textAlign: 'center', padding: 40, color: 'rgba(255,255,255,0.4)' },
  statusBadge: { padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 500, border: 'none', cursor: 'pointer', outline: 'none' },
  iconBtn: { background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', padding: 4, borderRadius: 4 },
  pagination: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 20 },
  pageBtn: { padding: '6px 10px', background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, color: '#fff', cursor: 'pointer' },
  pageInfo: { color: 'rgba(255,255,255,0.5)', fontSize: 13 },
  adCard: { background: 'rgba(0,0,0,0.4)', borderRadius: 12, padding: 24, marginBottom: 20, border: '1px solid rgba(0,255,70,0.1)' },
  adCardHeader: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 },
  adCardTitle: { color: '#fff', fontSize: 17, fontWeight: 600, flex: 1 },
  activeBadge: { padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 500, background: 'rgba(0,255,70,0.1)', color: '#0F0', border: '1px solid rgba(0,255,70,0.2)' },
  adInfoGrid: { display: 'grid', gap: 12, marginBottom: 20 },
  infoRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid rgba(0,255,70,0.06)' },
  infoLabel: { color: 'rgba(255,255,255,0.45)', fontSize: 13 },
  infoValueWrap: { display: 'flex', alignItems: 'center', gap: 8 },
  infoValue: { color: '#fff', fontSize: 13, fontFamily: 'monospace' },
  copySmallBtn: { display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', fontSize: 11 },
  eventsSection: { marginBottom: 20 },
  eventsTitle: { color: 'rgba(255,255,255,0.7)', fontSize: 14, fontWeight: 500, marginBottom: 10 },
  eventsList: { display: 'flex', gap: 10, flexWrap: 'wrap' },
  eventTag: { background: 'rgba(0,255,70,0.06)', borderRadius: 8, padding: '8px 14px', display: 'flex', flexDirection: 'column', gap: 2, border: '1px solid rgba(0,255,70,0.1)' },
  eventName: { color: '#0F0', fontSize: 13, fontWeight: 600, fontFamily: 'monospace' },
  eventDesc: { color: 'rgba(255,255,255,0.4)', fontSize: 11 },
  codeSection: { marginBottom: 20 },
  codeHint: { color: 'rgba(255,255,255,0.4)', fontSize: 12, marginBottom: 10 },
  codeBlock: { background: 'rgba(0,0,0,0.3)', borderRadius: 8, padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, border: '1px solid rgba(0,255,70,0.12)' },
  codeText: { color: '#0F0', fontSize: 13, fontFamily: 'monospace', wordBreak: 'break-all' },
  copyCodeBtn: { display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', background: 'rgba(0,255,70,0.08)', border: '1px solid rgba(0,255,70,0.2)', borderRadius: 6, color: '#0F0', fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap', marginLeft: 12 },
  instructionBlock: { marginTop: 16 },
  instructionList: { color: 'rgba(255,255,255,0.55)', fontSize: 13, lineHeight: 2, paddingLeft: 20 },
  quickRef: { display: 'grid', gap: 8 },
  quickRefRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid rgba(0,255,70,0.06)' },
  quickRefLabel: { color: 'rgba(255,255,255,0.45)', fontSize: 13 },
  quickRefValueWrap: { display: 'flex', alignItems: 'center', gap: 8 },
  quickRefValue: { color: '#fff', fontSize: 13, fontFamily: 'monospace' },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 },
  modal: { background: 'rgba(0,10,0,0.6)', borderRadius: 16, padding: 32, maxWidth: 560, width: '100%', border: '1px solid rgba(0,255,70,0.15)', boxShadow: '0 0 40px rgba(0,255,70,0.1)' },
  modalTitle: { color: '#fff', fontSize: 18, fontWeight: 600, marginBottom: 20, fontFamily: 'monospace' },
  modalGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 },
  modalField: { display: 'flex', flexDirection: 'column', gap: 4 },
  modalFieldLabel: { color: 'rgba(255,255,255,0.4)', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 },
  modalFieldValue: { color: '#fff', fontSize: 14, fontFamily: 'monospace' },
  modalDescBlock: { background: 'rgba(0,0,0,0.3)', borderRadius: 8, padding: 16, marginBottom: 20, border: '1px solid rgba(0,255,70,0.08)' },
  modalDescLabel: { color: 'rgba(255,255,255,0.4)', fontSize: 12, marginBottom: 6 },
  modalDescText: { color: 'rgba(255,255,255,0.7)', fontSize: 14, lineHeight: 1.6 },
  modalActions: { display: 'flex', justifyContent: 'space-between' },
  modalDeleteBtn: { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, color: '#EF4444', fontSize: 13, cursor: 'pointer' },
  modalCloseBtn: { padding: '8px 24px', background: 'rgba(0,255,70,0.08)', border: '1px solid rgba(0,255,70,0.2)', borderRadius: 8, color: '#0F0', fontSize: 13, cursor: 'pointer' },
  logoutBtn: { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.15)', borderRadius: 8, color: '#EF4444', fontSize: 13, cursor: 'pointer', marginTop: 'auto', width: '100%', justifyContent: 'center' },
};
